import React, { useState, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import supabase from '../../supabase/supabase';
import { REPORTS_TABLE, logAdminAction } from '../../utils/auditLogger';
import { CHAT_TABLE, MODERATION_TABLE } from '../Chat/chatConstants';

const { FiAlertTriangle, FiCheck, FiSlash, FiTrash2, FiUserX } = FiIcons;

export default function ReportsQueueManager({ adminProfile }) {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchReports = async () => {
    const { data } = await supabase
      .from(REPORTS_TABLE)
      .select('*')
      .eq('status', 'pending')
      .order('created_at', { ascending: false });
    if (data) setReports(data);
  };

  useEffect(() => {
    fetchReports();
    const channel = supabase
      .channel('reports_realtime_admin')
      .on('postgres_changes', { event: '*', schema: 'public', table: REPORTS_TABLE }, fetchReports)
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, []);

  const handleDismissReport = async (reportId, nickname) => {
    await supabase.from(REPORTS_TABLE).update({ status: 'dismissed' }).eq('id', reportId);
    await logAdminAction(adminProfile?.username, 'DESESTIMAR_REPORTE', nickname, `Reporte #${reportId.slice(0, 6)} descartado`);
    fetchReports();
  };

  const handleAction = async (report, action) => {
    setLoading(true);
    if (report.message_id) {
      await supabase.from(CHAT_TABLE).update({ is_deleted: true, moderation_reason: 'Eliminado tras reporte' }).eq('id', report.message_id);
    }
    if (action === 'ban' || action === 'expel') {
      const expiresAt = action === 'expel' ? new Date(Date.now() + 15 * 60 * 1000).toISOString() : null;
      await supabase.from(MODERATION_TABLE).insert({
        subject_nickname: report.reported_nickname,
        action,
        reason: `Sanción originada por reporte comunitario: ${report.reason}`,
        expires_at: expiresAt
      });
      await logAdminAction(adminProfile?.username, action === 'ban' ? 'BLOQUEO_POR_REPORTE' : 'EXPULSION_POR_REPORTE', report.reported_nickname, `Motivo: ${report.reason}`);
    }
    await supabase.from(REPORTS_TABLE).update({ status: 'resolved', resolved_at: new Date().toISOString() }).eq('id', report.id);
    fetchReports();
    setLoading(false);
  };

  return (
    <section className="rounded-2xl border border-white/10 bg-[#111] p-5 space-y-4">
      <div className="flex items-center justify-between border-b border-white/5 pb-3">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-yellow-500/10 text-yellow-500">
            <SafeIcon icon={FiAlertTriangle} size={18} />
          </span>
          <div>
            <h3 className="font-semibold text-white text-sm sm:text-base">Cola de Reportes de Espectadores</h3>
            <p className="text-xs text-gray-500">Mensajes y usuarios denunciados por la comunidad en vivo</p>
          </div>
        </div>
        <span className="text-xs font-bold text-yellow-400 bg-yellow-500/10 px-2.5 py-1 rounded-lg">
          {reports.length} pendientes
        </span>
      </div>

      <div className="space-y-3 max-h-[450px] overflow-y-auto">
        {reports.map((report) => (
          <div key={report.id} className="p-4 bg-black/40 border border-white/5 rounded-xl space-y-3">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-xs font-bold text-red-400">Usuario reportado: @{report.reported_nickname}</p>
                <p className="text-[10px] text-gray-400 mt-0.5">Motivo: <span className="text-white font-medium">{report.reason}</span> (por @{report.reporter_nickname || 'Espectador'})</p>
              </div>
              <time className="text-[10px] text-gray-500">{new Date(report.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</time>
            </div>

            {report.reported_content && (
              <p className="p-2.5 bg-white/5 rounded-lg text-xs text-gray-300 italic border-l-2 border-yellow-500">
                "{report.reported_content}"
              </p>
            )}

            <div className="flex flex-wrap items-center justify-end gap-2 pt-1">
              <button
                disabled={loading}
                onClick={() => handleDismissReport(report.id, report.reported_nickname)}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-white/10 text-gray-400 hover:text-white text-xs transition-colors"
              >
                <SafeIcon icon={FiCheck} size={12} /> Descartar
              </button>
              <button
                disabled={loading}
                onClick={() => handleAction(report, 'expel')}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 text-xs font-medium transition-colors"
              >
                <SafeIcon icon={FiUserX} size={12} /> Expulsar 15m
              </button>
              <button
                disabled={loading}
                onClick={() => handleAction(report, 'ban')}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-red-600 hover:bg-red-700 text-white text-xs font-bold transition-all"
              >
                <SafeIcon icon={FiSlash} size={12} /> Bloquear Definitivo
              </button>
            </div>
          </div>
        ))}

        {reports.length === 0 && (
          <div className="text-center py-10 text-gray-500 text-xs">
            <SafeIcon icon={FiCheck} size={28} className="mx-auto text-green-500/50 mb-2" />
            No hay reportes de espectadores pendientes de revisión.
          </div>
        )}
      </div>
    </section>
  );
}